import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, Fieldset } from "@headlessui/react";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormError from "../../Form/FormError";
import WriteBlockGate from "../../Business/WriteBlockGate";
import { useAuthState } from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import { formatMoney } from "../../../../Utils";
import { getMaxPositionCost } from "../../../../Utils/payroll";

const FORM_ID = "payroll-opening-balance-form";

// Indonesian digit grouping, whole rupiah only: what the user types reads the
// same as the amounts printed everywhere else (Rp1.234.567).
const MONEY_FORMAT = {
  thousandSeparator: ".",
  decimalSeparator: ",",
  decimalScale: 0,
  allowNegative: false,
};

const MARRIAGE_OPTIONS = [
  { name: "TK", label: "payroll.openingBalance.options.TK" },
  { name: "K", label: "payroll.openingBalance.options.K" },
];

const DEPENDENT_OPTIONS = [0, 1, 2, 3].map((count) => ({
  name: String(count),
  label: String(count),
  value: count,
}));

const toInt = (value) => (Number.isFinite(value) ? Math.round(value) : 0);

/**
 * Add or change one employee's PPh 21 opening balance for a tax year: the
 * months this employer already paid outside PajakInd, as totals. The last tax
 * period settles against them, so the form keeps the same two caps on biaya
 * jabatan the server enforces and says what the cap is while it is typed.
 */
const PayrollOpeningBalanceModal = ({
  open,
  onClose,
  year,
  balance,
  employees,
  onSave,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const isEdit = Boolean(balance);

  const monthOptions = useMemo(() => {
    const format = new Intl.DateTimeFormat(i18n.language === "en" ? "en-US" : "id-ID", {
      month: "long",
    });
    // January to November: December is the last tax period itself, which the
    // payroll here always settles.
    return Array.from({ length: 11 }, (_, index) => ({
      value: index + 1,
      label: format.format(new Date(2000, index, 1)),
    }));
  }, [i18n.language]);

  const employeeOptions = useMemo(
    () =>
      employees.map((employee) => ({
        value: employee.id,
        label: `${employee.firstName} ${employee.lastName || ""}`.trim() +
          (employee.employeeID ? ` (${employee.employeeID})` : ""),
        employee,
      })),
    [employees],
  );

  const [employee, setEmployee] = useState(null);
  const [throughMonth, setThroughMonth] = useState(null);
  const [marriage, setMarriage] = useState(MARRIAGE_OPTIONS[0]);
  const [dependent, setDependent] = useState(DEPENDENT_OPTIONS[0]);
  const [grossIncome, setGrossIncome] = useState(undefined);
  const [positionCost, setPositionCost] = useState(undefined);
  const [pensionDeduction, setPensionDeduction] = useState(undefined);
  const [taxWithheld, setTaxWithheld] = useState(undefined);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    const option = balance
      ? employeeOptions.find((item) => item.value === balance.employeeID) || {
          value: balance.employeeID,
          label: balance.employeeID,
        }
      : null;
    setEmployee(option);
    setThroughMonth(
      balance ? monthOptions.find((item) => item.value === balance.throughMonth) : null,
    );
    setMarriage(
      MARRIAGE_OPTIONS.find((item) => item.name === balance?.marriageStatus) ||
        MARRIAGE_OPTIONS[0],
    );
    setDependent(DEPENDENT_OPTIONS[balance?.dependent ?? 0] || DEPENDENT_OPTIONS[0]);
    setGrossIncome(balance?.grossIncome);
    setPositionCost(balance?.positionCost);
    setPensionDeduction(balance?.pensionDeduction);
    setTaxWithheld(balance?.taxWithheld);
    setNote(balance?.note || "");
    setErrors({});
    setSubmitError(null);
    setDirty(false);
    // Seeded once per opening; later option changes must not wipe the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, balance]);

  const change = (setter) => (value) => {
    setter(value);
    setDirty(true);
  };

  const handleEmployeeChange = (option) => {
    setEmployee(option);
    setDirty(true);
    // PTKP as the employee record has it today - the usual answer, since the
    // status that counts is the one at the start of the year.
    const record = option?.employee;
    if (record) {
      setMarriage(
        MARRIAGE_OPTIONS.find((item) => item.name === record.marriageStatus) ||
          MARRIAGE_OPTIONS[0],
      );
      setDependent(DEPENDENT_OPTIONS[Math.min(Math.max(record.dependent || 0, 0), 3)]);
    }
  };

  const maxPositionCost = getMaxPositionCost(grossIncome, throughMonth?.value);

  const validate = () => {
    const next = {};
    if (!employee) next.employee = "payroll.openingBalance.validation.employee";
    if (!throughMonth) next.throughMonth = "payroll.openingBalance.validation.throughMonth";
    if (grossIncome === undefined) next.grossIncome = "payroll.openingBalance.validation.required";
    if (taxWithheld === undefined) next.taxWithheld = "payroll.openingBalance.validation.required";
    if (throughMonth && toInt(positionCost) > maxPositionCost) {
      next.positionCost = t("payroll.openingBalance.validation.positionCost", {
        amount: formatMoney(maxPositionCost),
      });
    }
    if (toInt(taxWithheld) > toInt(grossIncome)) {
      next.taxWithheld = "payroll.openingBalance.validation.taxWithheld";
    }
    if (note.length > 500) next.note = "payroll.openingBalance.validation.note";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await onSave({
        employeeID: employee.value,
        year,
        throughMonth: throughMonth.value,
        marriageStatus: marriage.name,
        dependent: dependent.value,
        grossIncome: toInt(grossIncome),
        positionCost: toInt(positionCost),
        pensionDeduction: toInt(pensionDeduction),
        taxWithheld: toInt(taxWithheld),
        note: note.trim() || null,
      });
      onClose(true);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setSaving(false);
    }
  };

  const money = (value, setter) => ({
    numberFormat: {
      ...MONEY_FORMAT,
      onValueChange: (values) => change(setter)(values.floatValue),
    },
    prefix: "Rp",
    inputMode: "numeric",
    value: value ?? "",
  });

  return (
    <SlideOver
      open={open}
      onClose={() => onClose(false)}
      dirty={dirty}
      title={
        isEdit
          ? "payroll.openingBalance.modal.titleEdit"
          : "payroll.openingBalance.modal.titleAdd"
      }
      footer={
        <div className="flex justify-between items-center">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={saving}
            disabled={writeBlocked || saving}
          >
            {t("common.save")}
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} className="space-y-5 mb-6" onSubmit={handleSubmit} noValidate>
        <WriteBlockGate>
          <FormError error={submitError} />
          <p className="text-sm text-gray-600">
            {t("payroll.openingBalance.modal.intro", { year })}
          </p>
          <Fieldset disabled={saving} className="space-y-5 data-[disabled]:opacity-50">
            <Field>
              <FormSelect
                required
                searchable
                label="payroll.openingBalance.fields.employee"
                placeholder="payroll.openingBalance.placeholders.employee"
                options={employeeOptions}
                value={employee}
                onChange={handleEmployeeChange}
                disabled={isEdit}
                error={errors.employee}
              />
            </Field>

            <Field>
              <FormSelect
                required
                label="payroll.openingBalance.fields.throughMonth"
                hint="payroll.openingBalance.hints.throughMonth"
                placeholder="payroll.openingBalance.placeholders.throughMonth"
                options={monthOptions}
                value={throughMonth}
                onChange={change(setThroughMonth)}
                error={errors.throughMonth}
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormRadioGroup
                label="payroll.openingBalance.fields.marriageStatus"
                name="marriageStatus"
                variant="segmented"
                verticalSpace="py-1.5"
                options={MARRIAGE_OPTIONS}
                value={marriage}
                onChange={change(setMarriage)}
              />
              <FormRadioGroup
                label="payroll.openingBalance.fields.dependent"
                name="dependent"
                variant="segmented"
                verticalSpace="py-1.5"
                gridClassName="grid-cols-4"
                options={DEPENDENT_OPTIONS}
                value={dependent}
                onChange={change(setDependent)}
              />
            </div>

            <FormInput
              required
              name="grossIncome"
              label="payroll.openingBalance.fields.grossIncome"
              error={errors.grossIncome}
              {...money(grossIncome, setGrossIncome)}
            />

            <FormInput
              name="positionCost"
              label="payroll.openingBalance.fields.positionCost"
              hint={
                throughMonth
                  ? t("payroll.openingBalance.hints.positionCostMax", {
                      amount: formatMoney(maxPositionCost),
                    })
                  : null
              }
              labelAction={
                throughMonth && grossIncome ? (
                  <button
                    type="button"
                    onClick={() => change(setPositionCost)(maxPositionCost)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    {t("payroll.openingBalance.actions.fillMax")}
                  </button>
                ) : null
              }
              error={errors.positionCost}
              {...money(positionCost, setPositionCost)}
            />

            <FormInput
              name="pensionDeduction"
              label="payroll.openingBalance.fields.pensionDeduction"
              {...money(pensionDeduction, setPensionDeduction)}
            />

            <FormInput
              required
              name="taxWithheld"
              label="payroll.openingBalance.fields.taxWithheld"
              error={errors.taxWithheld}
              {...money(taxWithheld, setTaxWithheld)}
            />

            <div>
              <label
                htmlFor="payroll-opening-balance-note"
                className="block text-sm font-medium leading-5 text-gray-700 mb-1"
              >
                {t("payroll.openingBalance.fields.note")}
              </label>
              <textarea
                id="payroll-opening-balance-note"
                rows={2}
                maxLength={500}
                value={note}
                onChange={(event) => change(setNote)(event.target.value)}
                className="block w-full rounded-md shadow-sm text-sm text-gray-900 border-0 ring-1 ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-500"
              />
            </div>
          </Fieldset>
        </WriteBlockGate>
      </form>
    </SlideOver>
  );
};

export default PayrollOpeningBalanceModal;
